import * as React from "react";
import * as ReactDOM from "react-dom";

// "What's new" guided tour. Plays once on first load after a release (per
// browser), can be skipped any time, and replays from the What's new button.
// Each step spotlights one element (found by its data-tour attribute or a
// selector) and explains it in a card beside it; a step whose element isn't
// on screen shows its card centered instead.

export type TourStep = {
  /** CSS selector for the element to spotlight, or a list tried in order
      (first one on screen wins); omit for a centered card. */
  target?: string | string[];
  /** Attribute that ties the target to siblings: the spotlight covers every
      element sharing the target's value (e.g. both bars of a link). */
  group?: string;
  title: string;
  body: React.ReactNode;
};

// Bump when the tour's content changes enough to show it to everyone again.
const SEEN_KEY = "roadmap.tour.whatsNew.v2";

export function hasSeenTour(): boolean {
  try {
    return window.localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return true; // storage blocked: never nag on every load
  }
}

export function markTourSeen(): void {
  try {
    window.localStorage.setItem(SEEN_KEY, "1");
  } catch {
    // Storage blocked: the tour may play again next time; harmless.
  }
}

export const WHATS_NEW_STEPS: TourStep[] = [
  {
    title: "What's new in the Digital Roadmap",
    body: "A quick tour of quarter ranges, grouping, workstreams, dependencies, new statuses and Excel export. Use the arrow keys or the buttons; Esc closes it.",
  },
  {
    target: '[data-tour="filter-quarter"]',
    title: "Pick a range of quarters",
    body: "Pick one quarter to zoom the timeline to it, or a first and last quarter to see the range between them. Click an end quarter again to drop it.",
  },
  {
    target: '[data-tour="group-by"]',
    title: "Group by team, pod and workstream",
    body: "Tick any combination. They nest in that order, and your choice is remembered next time. Links you share keep the grouping.",
  },
  {
    target: [".ws-block[data-ws-multi]", ".ws-block"],
    title: "Workstreams",
    body: "A workstream groups related work, even across teams and pods. Drag a bar up or down onto another workstream block to move it there; Undo is in the message that follows.",
  },
  {
    target: '[data-tour="filter-workstream"]',
    title: "Filter by workstream",
    body: "Set a workstream in an initiative's drawer or the New initiative form, then filter by it here.",
  },
  {
    // A pair that's already linked shows the line; any bar will do otherwise.
    target: [".bar-row[data-link-pair]", ".bar-row"],
    group: "data-link-pair",
    title: "Link dependencies",
    body: "Hover a bar and drag the small dot past its right end onto another bar: that bar now depends on this one. Click a line between two bars to remove the link.",
  },
  {
    target: '[data-tour="legend"]',
    title: "New statuses and colors",
    body: "On Hold is yellow, Canceled is gray and struck through, and anything past its target date that isn't finished shows red as Overdue.",
  },
  {
    target: '[data-tour="count"]',
    title: "What's on screen",
    body: "The timeline shows four quarters at a time. When some work falls outside, this says how many of your initiatives are in view.",
  },
  {
    target: '[data-tour="export"]',
    title: "Export to Excel",
    body: "Download the entire roadmap, your filtered view, or exactly what's on the timeline, with each initiative's latest weekly update.",
  },
  {
    target: '[data-tour="new"]',
    title: "Faster new initiatives",
    body: "The form remembers your last team, pod and owner, starts on today, and keeps the target after the start.",
  },
  {
    target: '[data-tour="whats-new"]',
    title: "That's it",
    body: "Replay this tour any time from What's new.",
  },
];

const PAD = 6;      // spotlight padding around the target
const GAP = 12;     // space between spotlight and card
const MARGIN = 12;  // keep the card inside the viewport
const CARD_W = 340;

type Box = { left: number; top: number; right: number; bottom: number; width: number; height: number };

const laidOut = (el: HTMLElement) => {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
};

function findTarget(target?: string | string[], group?: string): HTMLElement | null {
  if (!target) return null;
  // First selector with a match that is actually laid out (a hidden element has no box).
  for (const selector of Array.isArray(target) ? target : [target]) {
    const els = Array.from(document.querySelectorAll<HTMLElement>(selector)).filter(laidOut);
    if (!els.length) continue;
    if (!group) return els[0];
    // Grouped: the most compact group reads best (a link between neighbours,
    // not one that spans the page).
    return els.reduce((best, el) => (measure(el, group).height < measure(best, group).height ? el : best));
  }
  return null;
}

// The target's box, grown to cover every element in its group.
function measure(el: HTMLElement, group?: string): Box {
  const value = group ? el.getAttribute(group) : null;
  const els = value
    ? Array.from(document.querySelectorAll<HTMLElement>(`[${group}="${value}"]`)).filter(laidOut)
    : [el];
  const rects = (els.length ? els : [el]).map(e => e.getBoundingClientRect());
  const left = Math.min(...rects.map(r => r.left));
  const top = Math.min(...rects.map(r => r.top));
  const right = Math.max(...rects.map(r => r.right));
  const bottom = Math.max(...rects.map(r => r.bottom));
  return { left, top, right, bottom, width: right - left, height: bottom - top };
}

export function WhatsNewTour({
  open,
  steps = WHATS_NEW_STEPS,
  onClose,
}: {
  open: boolean;
  steps?: TourStep[];
  onClose: () => void;
}) {
  const [index, setIndex] = React.useState(0);
  const [rect, setRect] = React.useState<Box | null>(null);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const step = steps[index];
  const last = index === steps.length - 1;

  React.useEffect(() => { if (open) setIndex(0); }, [open]);

  // Find, scroll to and measure this step's target; keep it measured as the
  // page scrolls or resizes.
  React.useLayoutEffect(() => {
    if (!open) return;
    const el = findTarget(step?.target, step?.group);
    if (el) el.scrollIntoView({ block: "center", inline: "nearest" });
    const remeasure = () => {
      const t = findTarget(step?.target, step?.group);
      setRect(t ? measure(t, step?.group) : null);
    };
    remeasure();
    // Once more after the scroll settles, in case no scroll event follows it.
    const frame = window.requestAnimationFrame(remeasure);
    window.addEventListener("resize", remeasure);
    window.addEventListener("scroll", remeasure, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("scroll", remeasure, true);
    };
  }, [open, index, step]);

  React.useEffect(() => {
    if (open) cardRef.current?.focus();
  }, [open, index]);

  const close = React.useCallback(() => {
    markTourSeen();
    onClose();
  }, [onClose]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); close(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); if (last) close(); else setIndex(i => i + 1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); setIndex(i => Math.max(0, i - 1)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, last, close]);

  if (!open || !step) return null;

  // Card below the target when it fits, else above; centered when there's no target.
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(CARD_W, vw - 2 * MARGIN);
  let cardStyle: React.CSSProperties;
  if (rect) {
    const left = Math.max(MARGIN, Math.min(rect.left, vw - width - MARGIN));
    const below = rect.bottom + PAD + GAP;
    const roomBelow = vh - below - MARGIN;
    cardStyle = roomBelow >= 160
      ? { left, top: below, width }
      : { left, bottom: vh - (rect.top - PAD - GAP), width };
  } else {
    cardStyle = { left: "50%", top: "50%", transform: "translate(-50%, -50%)", width };
  }

  return ReactDOM.createPortal(
    <div className="tour" aria-live="polite">
      {/* Blocks the page while the tour runs; clicking it does nothing. */}
      <div className={`tour-backdrop ${rect ? "" : "is-dim"}`} />
      {rect && (
        <div
          className="tour-spotlight"
          style={{ left: rect.left - PAD, top: rect.top - PAD, width: rect.width + 2 * PAD, height: rect.height + 2 * PAD }}
        />
      )}
      <div
        ref={cardRef}
        className="tour-card"
        style={cardStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-title"
        tabIndex={-1}
      >
        <div className="tour-step">{index + 1} of {steps.length}</div>
        <h2 id="tour-title" className="tour-title">{step.title}</h2>
        <p className="tour-body">{step.body}</p>
        <div className="tour-actions">
          <button type="button" className="btn sm ghost" onClick={close}>{last ? "Close" : "Skip tour"}</button>
          <span className="tour-spacer" />
          {index > 0 && (
            <button type="button" className="btn sm" onClick={() => setIndex(i => i - 1)}>Back</button>
          )}
          <button type="button" className="btn sm primary" onClick={() => (last ? close() : setIndex(i => i + 1))}>
            {last ? "Done" : "Next"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
